import React, { useEffect, useMemo, useState } from "react";
import CustomButton from "../components/CustomButton";
import { useNavigate, useParams } from "react-router-dom";
import { getPaperInsights, stopAICheck, updateInsight } from "../api/Api";
import { showToast } from "../utils/toastUtils";
// import ReferenceResultCard from "../components/cards/ReferenceResultCard";
import { CgSandClock } from "react-icons/cg";
import { SlRefresh } from "react-icons/sl";
import { FaRegHandPointRight } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import styles from "./Preview.module.css";
import PaperInsights from "../components/PaperInsights";
import { useSearchParams } from "react-router-dom";
import { PostCheck } from "../components/feature/postchecks/Postcheck";

function Preview() {
  // /journals/${values.journal_id}/preview/${paperId}?upload=${journalName}
  const [activeStep, setActiveStep] = React.useState(0);
  const { paperId } = useParams();

  return (
    <div className="p-4 px-xl-5 h-100">
      {activeStep === 0 && (
        <Insights setActiveStep={setActiveStep} paperId={paperId} />
      )}
      {activeStep === 1 && <PostCheck paperId={paperId} />}
    </div>
  );
}

export default Preview;

const Insights = ({ setActiveStep, array, paperId }) => {
  const [isReady, setIsReady] = useState(false); // to check if the insights are ready
  const [insights, setInsightIssues] = useState([]);
  // const [referenceList, setReferenceList] = useState([]);
  // const [checkedIssues, setCheckedIssues] = useState([]);
  const [display, setDisplay] = useState(false); // to check if the paper is deleted
  const [executionId, setExecutionId] = useState(null); // to check if the paper is deleted
  const [isStopping, setIsStopping] = useState(false);

  const [searchParams] = useSearchParams();
  const upload = useMemo(() => !!searchParams.get("upload"), [searchParams]); // to check if user comes from manuscript upload page
  const navigate = useNavigate();

  // function to fetch insights and set them in the state
  const fetchAndSetInsights = React.useCallback(
    async (paperId, displayNoticePage) => {
      try {
        const response = await getPaperInsights(paperId);
        if (response.status === 200) {
          setIsReady(true);
          setInsightIssues(response.data.insights_list);
        }
        if (response.status === 202) {
          setIsReady(false);
          setDisplay(response.data.auto_delete_file);
          setExecutionId(response.data.execution_id);
        }
      } catch (error) {
        console.error("Error fetching insights:", error);
      }
    },
    [setIsReady, setInsightIssues, setDisplay, setExecutionId]
  );

  useEffect(() => {
    if (paperId) {
      fetchAndSetInsights(paperId, upload);
      return () => setIsReady(false);
    }
  }, [paperId, upload, fetchAndSetInsights]);

  const handleCheckChange = (insight_id, value) => {
    const payload = {
      insight_id: insight_id,
      is_marked_as_fixed: value,
    };

    updateInsight(paperId, payload).then((response) => {
      console.log(response);
      if (response.status === 200) {
        // setCheckedIssues((prevCheckedIssues) => ({
        //   ...prevCheckedIssues,
        //   [insight_id]: value,
        // }));
        setInsightIssues((prevInsights) =>
          prevInsights.map((issue) =>
            issue.id === insight_id
              ? { ...issue, is_marked_as_fixed: value }
              : issue
          )
        );
      } else {
        showToast(response.message || "Something went wrong", "error");
      }
    });
  };

  const handleStopAnalysis = async () => {
    if (!executionId) {
      showToast("No analysis in progress", "error");
      return;
    }
    setIsStopping(true);
    try {
      const response = await stopAICheck(executionId);
      if (response.status === 200) {
        showToast("Analysis stopped successfully", "success");
        navigate("/manuscript-validator");
      } else {
        showToast("Failed to stop analysis", "error");
      }
    } catch (error) {
      console.error("Error stopping analysis:", error);
      showToast("Something went wrong while stopping analysis", "error");
    } finally {
      setIsStopping(false);
    }
  };
  

  useEffect(() => {
    if (!isReady) {
      const statusInterval = setInterval(() => {
        fetchAndSetInsights(paperId, upload);
      }, 5000);
  
      return () => clearInterval(statusInterval);
    }
  }, [paperId, isReady, upload, fetchAndSetInsights]);

  // should be dynamic
  if (!array)
    array = [
      { title: "Home", link: "/" },
      { title: "Preview", link: "/preview" },
      { title: "Insights", link: "/preview/insights" },
    ];

  if (!isReady) {
    return (
      <>
        <div>
          <p className="p1 fw-bold mb-1">Analyzing Your Manuscript</p>
          <p className="p2 mb-4 fw-normal">
            Our Al is checking your document against journal guidelines.This
            typically takes 2-3 minutes.
          </p>
        </div>

        <div className="secondary-box bg-white py-4 px-5 rounded-4 overflow-hidden">
          <div className="d-flex mb-3 align-items-center ps-2">
            <div className="d-flex align-items-center justify-content-center">
              <CgSandClock color="#0BA7FF" size={20} />
            </div>
            <h6 className="mb-0">Processing Status</h6>
          </div>
          <p className="p3 mb-2 fw-normal ps-3">
            We're analyzing your manuscript to ensure it meets all requirements.
          </p>
          <p className="p3 fw-normal ps-3">
            Once Complete, we’ll show a summary of formatting issues and
            recommendations.
          </p>

          <div className="d-flex w-100 align-items-center mt-4 gap-2 ps-3">
            <div className={styles.customSpinner}></div>
            <div>
              <p
                style={{ fontSize: "17px" }}
                className="fw-semibold mb-0 opacity-75"
              >
                Analyzing Document Structure
              </p>
              <p className="p4 mb-0 opacity-50">
                Checking document formatting, sections, and layout
              </p>
            </div>
          </div>

          <div className="d-flex w-100 align-items-center mt-2 gap-2 ps-3">
            <div className={styles.customSpinner}></div>
            <div>
              <p
                style={{ fontSize: "17px" }}
                className="fw-semibold mb-0 opacity-75"
              >
                Validating References
              </p>
              <p className="p4 mb-0 opacity-50">
                Checking citations and bibliography format
              </p>
            </div>
          </div>

          <div className="d-flex w-100 align-items-center mt-2 gap-2 ps-3">
            <div className={styles.customSpinner}></div>
            <div>
              <p
                style={{ fontSize: "17px" }}
                className="fw-semibold mb-0 opacity-75"
              >
                Applying Journal Guidelines
              </p>
              <p className="p4 mb-0 opacity-50">
                Comparing with selected journal requirements
              </p>
            </div>
          </div>

          <div className="d-flex w-100 align-items-center mt-2 gap-2 ps-3">
            <div className={styles.customSpinner}></div>
            <div>
              <p
                style={{ fontSize: "17px" }}
                className="fw-semibold mb-0 opacity-75"
              >
                Running AI Checks
              </p>
              <p className="p4 mb-0 opacity-50">
                Advanced analysis with our Al models
              </p>
            </div>
          </div>

          <div className="d-flex w-100 align-items-center mt-2 gap-2 ps-3">
            <div className={styles.customSpinner}></div>
            <div>
              <p
                style={{ fontSize: "17px" }}
                className="fw-semibold mb-0 opacity-75"
              >
                Generating Recommendations
              </p>
              <p className="p4 mb-0 opacity-50">
                Creating personalized improvement suggestions
              </p>
            </div>
          </div>

          {/* sparkle */}
          <div className="d-flex justify-content-between align-items-center mt-4 position-relative pt-4 ps-3">
            <hr
              style={{
                border: "1px solid rgb(221, 221, 221)",
                left: "-200px",
                width: "2000px",
              }}
              className="position-absolute top-0 my-0"
            />
            <div className="d-flex w-100 align-items-center gap-2 ms-3">
              <div
                className={`d-flex justify-content-center ${styles.spinAnimation}`}
              >
                <SlRefresh size={20} />
              </div>
              <p className="mb-0">Estimated time remaining: 1-2 minutes</p>
            </div>
            <CustomButton
              variant="gray-outline"
              className="p4"
              disabled={isStopping}
              onClick={handleStopAnalysis}
            >
              Stop Analysis
            </CustomButton>
          </div>
        </div>

        <div
          style={{ background: "#E9F2FF", border: "1px solid var(--cyan)" }}
          className="d-flex flex-column mt-4 rounded-4 p-3"
        >
          <p className="p3 fw-medium mb-1">While waiting....</p>
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div className="d-flex align-items-start gap-2">
              <FaRegHandPointRight size={24} color="#0BA7FF" className="mt-1" />
              <p className="mb-0 p3">
                Check out our Journal Suggestions tool to find the perfect
                journal for your publication.
              </p>
            </div>
            <CustomButton
              variant="gray-outline"
              className="p4 flex-row-reverse gap-4"
              style={{
                color: "var(--blue)",
                background: "white",
                border: "1px solid var(--blue)",
              }}
              onClick={() => navigate("/journal-recommender")}
              icon={<FiExternalLink size={20} />}
            >
              Open Journal Recommender
            </CustomButton>
          </div>
        </div>
      </>
    );
  }
  if (upload && display) return <PaperIsDeleted />; // to show the paper is deleted automatically

  return (
    <PaperInsights
      handleCheckChange={handleCheckChange}
      insights={insights}
      setActiveStep={setActiveStep}
    />
  );
};

export const ProgressBar = ({ progress, height = 12 }) => {
  return (
    <div style={{ background: "#E8E8E8" }} className="w-100 rounded-3">
      <div
        className="rounded-3"
        style={{
          width: `${progress}%`,
          height: `${height}px`,
          background: "var(--blue)",
        }}
      ></div>
    </div>
  );
};

// for paper deleted page
const PaperIsDeleted = () => {
  const navigate = useNavigate();
  const { paperId, journalId } = useParams();

  return (
    <div className="d-flex flex-column align-items-center h-100 justify-content-center">
      <div className="primary-box rounded-4 py-4 px-5 text-center">
        <h4 className="text">AI Analysis is Completed</h4>
        <p
          className="p1 fw-normal text-secondary"
          style={{ textWrap: "balance", maxWidth: "400px" }}
        >
          We've safely deleted your manuscript from our severs. Click below to
          view results.
        </p>
        <CustomButton
          className="mx-auto"
          onClick={() => {
            navigate(
              `/journals/${journalId}/preview/${paperId}`,
              { replace: true } // replace the current entry in the history stack
            );
          }}
        >
          View Results
        </CustomButton>
      </div>
    </div>
  );
};
